/**
 * Maps an agent activity status `code` (+ optional params) to a localized label.
 *
 * The server (and the historical tool-call reconstruction in ChatMessages) tag
 * each activity with a stable, language-neutral code. This is the single place
 * that turns those codes into user-facing strings via i18n, so the same
 * vocabulary is shared by the live streaming status and the persisted history.
 *
 * `fallback` is the server-provided English message; it is returned for any
 * unknown code so we never render an empty status.
 */
type TranslateFn = (key: string, named?: Record<string, unknown>) => string

export interface ActivityStatusParams {
  query?: string
  issue?: string
  count?: number
}

export function activityStatusLabel(
  t: TranslateFn,
  code: string | undefined,
  params: ActivityStatusParams = {},
  fallback = '',
): string {
  const k = (key: string, named?: Record<string, unknown>) => t(`webChat.activity.${key}`, named)
  const query = params.query?.trim()
  const issue = params.issue?.trim()
  const count = params.count ?? 0

  switch (code) {
    case 'help_checking':
      return query ? k('helpCheckingQuery', { query }) : k('helpChecking')
    case 'document_reading':
      return k('documentReading')
    case 'legal_reference_reading':
      return query ? k('legalReferenceReadingQuery', { query }) : k('legalReferenceReading')
    case 'legal_searching':
      if (count > 1) return k('legalTopicsSearching', { count })
      return query ? k('legalProvisionsSearchingQuery', { query }) : k('legalProvisionsSearching')
    case 'query_legal_catalog_running':
      return k('quickResearchRunning')
    case 'query_legal_catalog_done':
      return k('quickResearchDone')
    case 'legal_researching':
      return k('legalResearching')
    case 'legal_evaluating':
      return k('legalEvaluating')
    case 'legal_basis_researching':
      return issue ? k('legalBasisResearchingIssue', { issue }) : k('legalBasisResearching')
    case 'legal_reading':
      return count === 1
        ? k('legalProvisionReadingOne', { count })
        : k('legalProvisionReadingMany', { count })
    case 'documents_searching':
      return query ? k('documentsSearchingQuery', { query }) : k('documentsSearching')
    case 'workflow_searching':
      return query ? k('workflowSearchingQuery', { query }) : k('workflowSearching')
    case 'legal_validating':
      return k('legalValidating')
    case 'tool_running':
      return k('toolRunning')
    case 'searching':
      return k('searching')
    default:
      return fallback || k('searching')
  }
}
